import {
  useGetIpAddressRestrictionSetting,
  useUpdateIpAddressRestrictionSettingService,
} from '@/context/services/ip_address_restriction/ip_address_restriction_setting.service';
import {
  ipRestrictionFromApiBoolean,
  ipRestrictionToApiBoolean,
  type IpRestrictionRadioValue,
} from '@/features/ip_address_whitelists/components/RestrictIpSetting/hooks/ipRestrictionRadio';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import { useToastAPI } from '@moneyforward/ap-frontend-components';
import { useCallback, useMemo } from 'react';

export const useIpAddressRestrictionSetting = (
  onSubmitCallback: () => void,
  setInlineError: (error: Error) => void,
  officeId: string
) => {
  const { data } = useGetIpAddressRestrictionSetting();
  const { mutateAsync } = useUpdateIpAddressRestrictionSettingService(officeId);
  const notify = useToastAPI();
  const { t } = useTranslation();
  const { send } = useErrorTrackingSystem();

  const ipRestrictionServerMode = useMemo(() => {
    const isIpAddressRestrictionEnabled =
      data?.ip_address_restriction_enabled ?? false;
    return ipRestrictionFromApiBoolean(isIpAddressRestrictionEnabled);
  }, [data?.ip_address_restriction_enabled]);

  const saveIpAddressRestrictionSetting = useCallback(
    async (mode: IpRestrictionRadioValue): Promise<boolean> => {
      try {
        const isIpAddressRestrictionEnabled = ipRestrictionToApiBoolean(mode);
        const result = await mutateAsync({
          data: {
            ip_address_restriction_enabled: isIpAddressRestrictionEnabled,
          },
        });
        if (result.status === 'success') {
          notify.success({
            title: t('ip_address_whitelist_restrict_update_success'),
            duration: 5,
          });
          onSubmitCallback();
          return true;
        }

        const messages = result.errors?.messages ?? [];
        const title =
          messages.length > 0 ? messages.join('\n') : t('system_error');

        if (result.status === 'forbidden') {
          if (result.redirect_url) {
            location.href = result.redirect_url;
          } else {
            onSubmitCallback();
            setInlineError(new Error(title));
            return false;
          }
        }

        notify.error({ title, duration: 5 });
        onSubmitCallback();
        return false;
      } catch (error: unknown) {
        if (error instanceof Error) {
          send(error);
        } else {
          send(new Error(String(error)));
        }
        notify.error({
          title: t('system_error'),
          duration: 5,
        });

        onSubmitCallback();
        return false;
      }
    },
    [mutateAsync, notify, onSubmitCallback, send, setInlineError, t]
  );

  return {
    ipRestrictionServerMode,
    saveIpAddressRestrictionSetting,
  };
};
